/**
 * Estado do editor de régua — o mesmo desenho do editor de landing page.
 *
 * O que este hook garante e o componente não precisa saber:
 *
 *  - **A cópia do servidor só entra quando não há edição por perder** — e a
 *    versão recém-salva é registrada como adotada, para um refetch não
 *    reimplantar o passado por cima do presente.
 *  - **Desfazer em passos humanos**: mexidas em sequência (arrastar um nó,
 *    digitar um assunto) viram um passo só na janela de 600ms.
 *  - **Validação contínua com as MESMAS funções do servidor** (`validarRegua`
 *    do shared) — o operador descobre o problema enquanto desenha, não no
 *    clique de ativar.
 *  - **Atalhos**: Ctrl+S salva, Ctrl+Z desfaz, Esc solta a seleção, Delete
 *    remove a etapa selecionada (nunca enquanto se digita num campo).
 *  - **beforeunload** quando há rascunho — fechar a aba com uma régua não
 *    salva era perda silenciosa na tela antiga.
 */

import { etapaPadrao, validarRegua, type Etapa, type Ramo, type TipoEtapa } from '@egba-do/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { api, type Item } from '../../api/cliente'
import type { Detalhe } from './tipos'

interface ProblemaRegua {
  etapaId: string | null
  codigo: string
  mensagem: string
}

export function useEditorRegua(id: string) {
  const qc = useQueryClient()

  const [etapas, setEtapas] = useState<Etapa[]>([])
  const [selecionada, setSelecionada] = useState<string | null>(null)
  const [sujo, setSujo] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const historico = useRef<Etapa[][]>([])
  const ultimoPasso = useRef(0)
  const versaoAdotada = useRef<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['campanha', id],
    queryFn: () => api.get<Item<Detalhe>>(`/api/v1/campanhas/${id}`),
    // Uma tentativa extra cobre soluço de rede; insistir num 404 só adia a
    // tela de "não encontrada".
    retry: 1,
  })

  useEffect(() => {
    if (!data || sujo || versaoAdotada.current === data.dados.atualizadoEm) return
    versaoAdotada.current = data.dados.atualizadoEm
    historico.current = []
    setEtapas(data.dados.etapas)
  }, [data, sujo])

  const campanha = data?.dados ?? null
  const somenteLeitura = campanha?.status === 'encerrada'

  const validacao = useMemo(() => validarRegua(etapas), [etapas])

  const problemasPorEtapa = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const p of validacao.problemas as ProblemaRegua[]) {
      if (!p.etapaId) continue
      m.set(p.etapaId, [...(m.get(p.etapaId) ?? []), p.mensagem])
    }
    return m
  }, [validacao])

  // ------------------------------------------------------------------ ações

  function alterar(proximas: Etapa[]): void {
    const agora = Date.now()
    if (agora - ultimoPasso.current > 600) {
      historico.current.push(etapas)
      if (historico.current.length > 60) historico.current.shift()
    }
    ultimoPasso.current = agora
    setEtapas(proximas)
    setSujo(true)
  }

  function desfazer(): void {
    const anterior = historico.current.pop()
    if (!anterior) return
    ultimoPasso.current = 0
    setEtapas(anterior)
    setSujo(true)
  }

  function adicionar(tipo: TipoEtapa): void {
    // Entra abaixo do nó mais baixo: um nó novo em cima de outro é um nó que o
    // operador não encontra.
    const y = etapas.reduce((max, e) => Math.max(max, e.posicao.y), 0) + (etapas.length ? 120 : 40)
    const nova = etapaPadrao(crypto.randomUUID(), tipo, 60, y)
    alterar([...etapas, nova])
    setSelecionada(nova.id)
  }

  function mover(idEtapa: string, x: number, y: number): void {
    alterar(etapas.map((e) => (e.id === idEtapa ? { ...e, posicao: { x, y } } : e)))
  }

  function conectar(de: string, quando: Ramo, para: string): void {
    alterar(
      etapas.map((e) =>
        e.id === de
          ? { ...e, proximas: [...e.proximas.filter((a) => a.quando !== quando), { para, quando }] }
          : e,
      ),
    )
  }

  function desconectar(de: string, quando: Ramo): void {
    alterar(
      etapas.map((e) =>
        e.id === de ? { ...e, proximas: e.proximas.filter((a) => a.quando !== quando) } : e,
      ),
    )
  }

  function removerSelecionada(): void {
    if (!selecionada) return
    alterar(
      etapas
        .filter((e) => e.id !== selecionada)
        // As arestas que apontavam para a etapa removida vão junto, senão o
        // fluxo guarda uma referência pendurada que o servidor rejeita.
        .map((e) => ({ ...e, proximas: e.proximas.filter((a) => a.para !== selecionada) })),
    )
    setSelecionada(null)
  }

  const salvar = useMutation({
    mutationFn: () => api.put<Item<unknown>>(`/api/v1/campanhas/${id}/regua`, { etapas }),
    onSuccess: () => {
      setSujo(false)
      setErro(null)
      void qc.invalidateQueries({ queryKey: ['campanha', id] })
      void qc.invalidateQueries({ queryKey: ['campanhas'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  // --------------------------------------------------------------- teclado

  // Sem lista de dependências de propósito: reinstalado a cada render, enxerga
  // sempre o estado atual — uma lista aqui é uma closure velha salvando a
  // régua errada.
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent): void {
      const alvo = e.target as HTMLElement | null
      const digitando =
        !!alvo &&
        (alvo.tagName === 'INPUT' ||
          alvo.tagName === 'TEXTAREA' ||
          alvo.tagName === 'SELECT' ||
          alvo.isContentEditable)
      const comando = e.ctrlKey || e.metaKey

      if (comando && e.key.toLowerCase() === 's') {
        e.preventDefault()
        if (sujo && !salvar.isPending && !somenteLeitura) salvar.mutate()
        return
      }
      if (comando && !e.shiftKey && e.key.toLowerCase() === 'z' && !digitando) {
        e.preventDefault()
        if (!somenteLeitura) desfazer()
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !digitando && selecionada) {
        e.preventDefault()
        if (!somenteLeitura) removerSelecionada()
        return
      }
      if (e.key === 'Escape' && !digitando) setSelecionada(null)
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  })

  useEffect(() => {
    if (!sujo) return
    function avisar(e: BeforeUnloadEvent): void {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [sujo])

  return {
    campanha,
    carregando: isLoading,
    falha: error ? ((error as Error).message ?? 'Falha ao carregar.') : null,
    somenteLeitura,
    etapas,
    selecionada,
    setSelecionada,
    sujo,
    erro,
    setErro,
    validacao,
    problemasPorEtapa,
    problemasGerais: (validacao.problemas as ProblemaRegua[]).filter((p) => !p.etapaId),
    temHistorico: historico.current.length > 0,
    alterar,
    desfazer,
    adicionar,
    mover,
    conectar,
    desconectar,
    removerSelecionada,
    salvar,
  }
}
